/**
 * Affiliates: shared words, colours and small controls used by the list
 * (/affiliates) and the person page (/affiliates/:id).
 */
import { useEffect, useState, type ComponentType } from "react";
import { BriefcaseBusiness, Check, ChevronDown, Clapperboard, Copy, Handshake, Share2 } from "lucide-react";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { dueLabel, softClass } from "@/features/command/ui";
import type { AffiliateChannel, AffiliateKind, AffiliateStatus, Handle, Rhythm, TeamMember } from "./types";

export const KINDS: AffiliateKind[] = ["partner", "pro", "creator", "casual"];

/** Fallback when the server's rhythm hasn't loaded (it carries the same words). */
export const RHYTHM_FALLBACK: Record<AffiliateKind, Rhythm> = {
  partner: { label: "Affiliate + JV partner", blurb: "Mails their list or hosts us. Touch weekly, check in every 2 weeks, gift every quarter." },
  pro: { label: "Pro affiliate", blurb: "Selling SyncSuite is part of their job (UGC, real affiliates). Check in every 2 weeks, fresh assets monthly." },
  creator: { label: "Content creator", blurb: "Shares now and then. Engage with their posts, check in every 6 weeks." },
  casual: { label: "Casual sharer", blurb: "\"Sure, I'll share it.\" Thank them for every referral, a light check-in each quarter." },
};

export const KIND_META: Record<AffiliateKind, { plural: string; color: string; icon: ComponentType<{ className?: string }>; iconClass: string }> = {
  partner: { plural: "Also JV partners", color: "violet", icon: Handshake, iconClass: "text-violet-300" },
  pro: { plural: "Pro affiliates", color: "amber", icon: BriefcaseBusiness, iconClass: "text-amber-300" },
  creator: { plural: "Content creators", color: "pink", icon: Clapperboard, iconClass: "text-pink-300" },
  casual: { plural: "Casual sharers", color: "sky", icon: Share2, iconClass: "text-sky-300" },
};

/**
 * JV partners have their own Partners area (2026-10-28). Here "partner" means
 * an affiliate who is ALSO a JV partner, so it says so whatever the server calls it.
 */
const LABEL_OVERRIDE: Partial<Record<string, string>> = { partner: "Affiliate + JV partner" };
const BLURB_OVERRIDE: Partial<Record<string, string>> = {
  partner: "Affiliates who are also JV partners (the partnership itself is run from Partners). Touch weekly, gift every quarter.",
};

export function kindLabel(kind: string, rhythm?: Partial<Record<string, Rhythm>> | null): string {
  return LABEL_OVERRIDE[kind] ?? rhythm?.[kind]?.label ?? RHYTHM_FALLBACK[kind as AffiliateKind]?.label ?? kind;
}

export function kindBlurb(kind: string, rhythm?: Partial<Record<string, Rhythm>> | null): string {
  return BLURB_OVERRIDE[kind] ?? rhythm?.[kind]?.blurb ?? RHYTHM_FALLBACK[kind as AffiliateKind]?.blurb ?? "";
}

export const CHANNEL_FALLBACK: Record<AffiliateChannel, string> = { syncsuite: "Our program", clickbank: "ClickBank", other: "Somewhere else" };
export const CHANNEL_COLOR: Record<AffiliateChannel, string> = { syncsuite: "indigo", clickbank: "orange", other: "zinc" };
export const CHANNELS: AffiliateChannel[] = ["syncsuite", "clickbank", "other"];

export const STATUSES: AffiliateStatus[] = ["prospect", "active", "quiet", "ended"];
export const STATUS_META: Record<AffiliateStatus, { label: string; color: string }> = {
  prospect: { label: "Prospect", color: "amber" },
  active: { label: "Active", color: "emerald" },
  quiet: { label: "Quiet", color: "zinc" },
  ended: { label: "Ended", color: "rose" },
};

export const PLATFORMS: Array<{ value: string; label: string }> = [
  { value: "instagram", label: "Instagram" },
  { value: "tiktok", label: "TikTok" },
  { value: "youtube", label: "YouTube" },
  { value: "facebook", label: "Facebook" },
  { value: "x", label: "X (Twitter)" },
  { value: "linkedin", label: "LinkedIn" },
  { value: "threads", label: "Threads" },
  { value: "reddit", label: "Reddit" },
  { value: "bluesky", label: "Bluesky" },
  { value: "website", label: "Website" },
  { value: "other", label: "Other" },
];
export function platformLabel(p: string | null | undefined): string {
  if (!p) return "Other";
  return PLATFORMS.find((x) => x.value === p.toLowerCase())?.label ?? p;
}

/** A link to someone's public profile from a stored handle. */
export function profileUrl(h: Handle): string | null {
  if (h.url) return h.url;
  const raw = String(h.handle ?? "").trim();
  if (/^https?:\/\//i.test(raw)) return raw;
  const x = raw.replace(/^@/, "");
  if (!x) return null;
  switch (String(h.platform ?? "").toLowerCase()) {
    case "instagram": case "ig": return `https://instagram.com/${x}`;
    case "tiktok": return `https://www.tiktok.com/@${x}`;
    case "youtube": return `https://www.youtube.com/@${x}`;
    case "facebook": return `https://facebook.com/${x}`;
    case "x": case "twitter": return `https://x.com/${x}`;
    case "linkedin": return `https://www.linkedin.com/in/${x}`;
    case "threads": return `https://www.threads.net/@${x}`;
    case "reddit": return `https://www.reddit.com/user/${x}`;
    case "bluesky": return `https://bsky.app/profile/${x}`;
    case "website": return `https://${x}`;
    default: return null;
  }
}

export const shareLink = (code: string) => `https://syncsuite.co/?ref=${code}`;

/** due_date is a plain day ("2026-10-27"); read it as local noon so it never slips a day. */
export function dueOf(day: string | null | undefined) {
  return dueLabel(day ? `${day.slice(0, 10)}T12:00:00` : null);
}

export const TONE_CLASS: Record<string, string> = {
  late: "text-rose-300", today: "text-amber-300", soon: "text-indigo-300", later: "text-zinc-400", none: "text-zinc-500",
};

/** The local day as YYYY-MM-DD (for comparing against due dates). */
export function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand("copy");
      ta.remove();
      return ok;
    } catch { return false; }
  }
}

/** Small "Copy" button that flips to a tick for a moment. */
export function CopyButton({ text, label = "Copy", className, onCopy }: { text: string; label?: string; className?: string; onCopy?: () => void }) {
  const [done, setDone] = useState(false);
  return (
    <button
      type="button"
      onClick={async (e) => {
        e.preventDefault();
        e.stopPropagation();
        onCopy?.();
        if (await copyText(text)) { setDone(true); setTimeout(() => setDone(false), 1400); }
      }}
      className={cn(
        "inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-xs font-medium text-zinc-400 transition-colors hover:bg-zinc-800/70 hover:text-zinc-100",
        done && "text-emerald-300 hover:text-emerald-300",
        className,
      )}
      title={done ? "Copied" : label}
    >
      {done ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
      {label && <span>{done ? "Copied" : label}</span>}
    </button>
  );
}

/** A coloured pill that is also a dropdown (click the value to change it). */
export function PillSelect<T extends string>({
  value, options, onChange, color, disabled, title, className,
}: {
  value: T;
  options: Array<{ value: T; label: string }>;
  onChange: (v: T) => void;
  color: string;
  disabled?: boolean;
  title?: string;
  className?: string;
}) {
  return (
    <span className={cn("relative inline-flex max-w-full items-center rounded-full ring-1 ring-inset", softClass(color), disabled && "opacity-60", className)}>
      <select
        value={value}
        disabled={disabled}
        title={title}
        aria-label={title}
        onChange={(e) => onChange(e.target.value as T)}
        onClick={(e) => e.stopPropagation()}
        className="max-w-full cursor-pointer appearance-none truncate rounded-full bg-transparent py-0.5 pl-2 pr-5 text-[11px] font-medium text-inherit outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/40 disabled:cursor-not-allowed"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value} className="bg-zinc-900 text-zinc-100">{o.label}</option>
        ))}
      </select>
      <ChevronDown className="pointer-events-none absolute right-1.5 h-3 w-3 opacity-70" />
    </span>
  );
}

// ─── Team (owner pickers) ────────────────────────────────────────────────────

let teamCache: Promise<TeamMember[]> | null = null;
function loadTeam(): Promise<TeamMember[]> {
  if (!teamCache) {
    teamCache = api.get<{ members: TeamMember[] }>("/team")
      .then((d) => (d.members ?? []).map((m) => ({ id: m.id, name: m.name, email: m.email, image: m.image })))
      .catch(() => { teamCache = null; return []; });
  }
  return teamCache;
}

export function useTeam(): TeamMember[] {
  const [team, setTeam] = useState<TeamMember[]>([]);
  useEffect(() => {
    let live = true;
    void loadTeam().then((t) => { if (live) setTeam(t); });
    return () => { live = false; };
  }, []);
  return team;
}

/** "Matthew" for a teammate, capitalised (some names are stored lower-case). */
export function memberName(name: string | null | undefined): string {
  const s = String(name ?? "").trim();
  return s ? s.replace(/\b\w/g, (c) => c.toUpperCase()) : "";
}
